import React, { Fragment, useMemo } from 'react';
import { parseMathContent } from '../utils/parseMathContent';
import { renderLatexToMathML } from '../utils/renderLatexToMathML';
interface MathTextProps {
  text: string;
  as?: 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
  className?: string;
  mathClassName?: string;
}

/**
 * Wrapper component that parses text content for LaTeX math expressions
 * and renders them using Temml (MathML output).
 *
 * Features:
 * - Handles ZWSP (\u200B) delimiters around math expressions
 * - Supports inline math ($...$) and display math ($$...$$)
 * - Preserves parent styles through className inheritance
 * - Customizable wrapper element through 'as' prop
 *
 * @example
 * <MathText as="h4" className="text-heading-sm-bold">
 *   방정식 $x^2 + y^2 = r^2$의 해를 구하시오
 * </MathText>
 */
export const MathText: React.FC<MathTextProps> = ({
  text,
  as: Component = 'span',
  className,
  mathClassName
}) => {
  const segments = useMemo(() => parseMathContent(text), [text]);
  return <Component className={className} data-sentry-element="Component" data-sentry-component="MathText" data-sentry-source-file="MathText.tsx">
      {segments.map((segment, index) => {
      if (segment.type === 'math') {
        return <MathSegmentView key={index} className={mathClassName} content={segment.content} display={segment.display} />;
      }
      // Render plain text safely without dangerouslySetInnerHTML
      return <Fragment key={index}>{segment.content}</Fragment>;
    })}
    </Component>;
};
interface MathSegmentViewProps {
  content: string;
  display: boolean;
  className?: string;
}
const MathSegmentView: React.FC<MathSegmentViewProps> = ({
  content,
  display,
  className
}) => {
  const html = useMemo(() => renderLatexToMathML(content, display), [content, display]);
  return <span className={className} dangerouslySetInnerHTML={{
    __html: html
  }} data-sentry-component="MathSegmentView" data-sentry-source-file="MathText.tsx" />;
};